import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  EditIcon,
  Row,
  Table,
  TableColumns,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTeachingBlockStore } from '../../TeachingBlockGrid/Contexts/TeachingBlockContext';
import { TeachingBlockVersionTableType } from '../graphql/types';

type TeachingBlockVersionDependenciesTableProps = {
  teachingBlockVersionUuid: string;
};

export const TeachingBlockVersionDependenciesTable = observer(
  ({ teachingBlockVersionUuid }: TeachingBlockVersionDependenciesTableProps) => {
    const { pimAuthClaims } = useAuthClaims();
    const { t } = useTranslation();
    const schoolYear = useSelectedSchoolYear();

    const store = useTeachingBlockStore();

    const { sorting, saveSorting } = useColumnsSort('teachingBlockVersionDependenciesTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

    // Klassen der Version aus dem Store (Lesemodell + Stammdaten); früher lud die Tabelle dafür das
    // große Versions-Dokument mit allen Klassen, Lehrkräften und Räumen samt deren Teilbäumen.
    const { classesData } = useLoadBasicData({ pause: !teachingBlockVersionUuid });
    const classes: TeachingBlockVersionTableType[] =
      classesData?.classes
        .filter((c) => store.classes.has(c.uuid))
        .map((c) => {
          const { html, label } = hexToColorOption(c.timetableConfig?.color ?? '');
          return {
            uuid: c.uuid,
            name: c.name,
            count: 0,
            shortName: c.shortName,
            color: {
              color: html,
              label,
            },
          };
        }) ?? [];

    function createColumns(): TableColumns<TeachingBlockVersionTableType>[] {
      return [
        {
          header: t('common.priority.short'),
          meta: {
            tooltip: t('common.priority.full'),
          },
          accessorKey: 'priority',
          id: 'priority',
          size: 30,
        },
        {
          header: t('subject.title', { count: 2 }),
          accessorKey: 'subjects',
          id: 'subjects',
          size: 130,
        },
        {
          header: t('classes.title', { count: 2 }),
          accessorKey: 'classes',
          id: 'classes',
          size: 130,
        },
        {
          header: t('common.type'),
          accessorKey: 'type',
          id: 'type',
          size: 130,
        },
        {
          header: t('common.comment'),
          accessorKey: 'comment',
          id: 'comment',
          size: 130,
        },
        {
          header: t('common.active.short'),
          meta: {
            tooltip: t('common.active.full'),
          },
          accessorKey: 'isActive',
          id: 'isActive',
          type: 'active',
        },
      ];
    }

    const columns = useMemo(createColumns, []);

    const createActionItems = useCallback(
      (_row: Row<TeachingBlockVersionTableType>): DropdownMenuItem[] => {
        return [
          {
            label: t('teachingBlockVersion.useAsTemplate'),
          },
          {
            label: t('common.activate'),
          },
          {
            label: t('common.delete'),
            type: 'default',
            color: 'error',
          },
        ];
      },
      [t],
    );

    function handleEdit() {
      console.log('edit clicked');
    }

    function handleAdd() {
      console.log('add clicked');
    }

    return (
      <Table<TeachingBlockVersionTableType>
        showBorderRadius
        showShadow
        canScroll
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        data={classes}
        columns={columns}
        sorting={sorting}
        onSortingChange={saveSorting}
        onAddClick={handleAdd}
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('dependencies.title', { count: 2 })} - ${t('common.schoolYear')} ${schoolYear?.shortName}`,
          filename: `${t('dependencies.title', { count: 2 })}_${schoolYear?.shortName}`,
        }}
        lastCol={(row) => {
          return (
            <ButtonGroup disabled={store.readonly}>
              <Button
                disabled={store.readonly}
                hierarchy='tertiary'
                icon={<EditIcon className='small' />}
                onClick={handleEdit}
              />
              <Dropdown
                noPadding
                disabled={store.readonly}
                trigger={
                  <Button
                    disabled={store.readonly}
                    hierarchy='secondary'
                    icon={<DotsHorizontalIcon className='small' />}
                  />
                }
              >
                <DropdownMenu data={createActionItems(row)} />
              </Dropdown>
            </ButtonGroup>
          );
        }}
      />
    );
  },
);
